import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "../../../axios";
import { FaUsers, FaCoins, FaCheckCircle, FaClock, FaPercentage, FaGift, FaWallet, FaShoppingCart } from "react-icons/fa";

const ReferralDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get("/admin/referral/stats");
      if (res.data?.result) {
        setStats(res.data.result);
      }
    } catch (error) {
      console.error("Error fetching referral stats", error);
      setError("Failed to load referral statistics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bgBlue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <p className="text-red-500 font-semibold mb-2">{error}</p>
          <button
            onClick={() => fetchStats()}
            className="px-4 py-2 bg-bgBlue text-white rounded-lg text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6">
      <h1 className="text-2xl font-bold text-bgBlue mb-6">Referral Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard
          icon={<FaUsers size={24} />}
          value={stats?.totalReferrals || 0}
          label="Total Referrals"
          color="text-bgBlue"
          bg="bg-blue-50"
        />
        <StatCard
          icon={<FaCheckCircle size={24} />}
          value={stats?.rewardedReferrals || 0}
          label="Rewarded"
          color="text-green-600"
          bg="bg-green-50"
        />
        <StatCard
          icon={<FaClock size={24} />}
          value={stats?.pendingReferrals || 0}
          label="Active"
          color="text-yellow-600"
          bg="bg-yellow-50"
        />
        <StatCard
          icon={<FaPercentage size={24} />}
          value={`${stats?.conversionRate || 0}%`}
          label="Conversion Rate"
          color="text-purple"
          bg="bg-purple-50"
        />
        <StatCard
          icon={<FaCoins size={24} />}
          value={stats?.totalCoinsDistributed || 0}
          label="Coins Distributed"
          color="text-orange-600"
          bg="bg-orange-50"
        />
      </div>

      {/* Distribution Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-bgBlue mb-4">Coin Distribution</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Total Referrer Rewards</span>
              <span className="font-bold text-bgBlue">{stats?.referrerCoinsDistributed || 0} coins</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Signup Bonuses (to referred users)</span>
              <span className="font-bold text-bgBlue">{stats?.referredBonusDistributed || 0} coins</span>
            </div>
          </div>
        </div>

        {/* Reward Breakdown */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-bold text-bgBlue mb-4">Referrer Reward Breakdown</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2">
                <FaGift className="text-green-600" />
                <span className="text-gray-600">Signup Rewards</span>
              </div>
              <span className="font-bold text-green-600">{stats?.rewardBreakdown?.signup || 0} coins</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2">
                <FaWallet className="text-blue-600" />
                <span className="text-gray-600">First Recharge Rewards</span>
              </div>
              <span className="font-bold text-blue-600">{stats?.rewardBreakdown?.firstRecharge || 0} coins</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-2">
                <FaShoppingCart className="text-purple-600" />
                <span className="text-gray-600">Plan Purchase Rewards</span>
              </div>
              <span className="font-bold text-purple-600">{stats?.rewardBreakdown?.planPurchase || 0} coins</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Referrers */}
      <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
        <h2 className="text-lg font-bold text-bgBlue mb-4">Top Referrers</h2>
        {stats?.topReferrers?.length > 0 ? (
          <div className="space-y-2">
            {stats.topReferrers.map((referrer, idx) => (
              <div key={referrer._id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                    idx === 0 ? "bg-yellow-500" : idx === 1 ? "bg-gray-400" : idx === 2 ? "bg-orange-400" : "bg-gray-300"
                  }`}>
                    {idx + 1}
                  </span>
                  <div>
                    <Link
                      to={`/dash/view-user/${referrer._id}`}
                      className="font-semibold text-bgBlue text-sm hover:underline"
                    >
                      {referrer.name || referrer.mobile || "—"}
                    </Link>
                    <p className="text-xs text-gray-400">{referrer.email}</p>
                  </div>
                </div>
                <span className="font-bold text-purple">{referrer.count} referrals</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-4">No referrers yet</p>
        )}
      </div>

      {/* Recent Referrals */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-bold text-bgBlue mb-4">Recent Referrals</h2>
        {stats?.recentReferrals?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2 text-gray-500">Referrer</th>
                  <th className="text-left py-3 px-2 text-gray-500">Referred User</th>
                  <th className="text-left py-3 px-2 text-gray-500">Status</th>
                  <th className="text-left py-3 px-2 text-gray-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentReferrals.map((ref) => (
                  <tr key={ref._id} className="border-b border-gray-100">
                    <td className="py-3 px-2 font-semibold text-bgBlue">
                      {ref.referrer?._id ? (
                        <Link
                          to={`/dash/view-user/${ref.referrer._id}`}
                          className="hover:underline"
                        >
                          {ref.referrer?.name || ref.referrer?.mobile || "—"}
                        </Link>
                      ) : (
                        ref.referrer?.name || ref.referrer?.mobile || "—"
                      )}
                    </td>
                    <td className="py-3 px-2">
                      {ref.referred?._id ? (
                        <Link
                          to={`/dash/view-user/${ref.referred._id}`}
                          className="font-semibold text-bgBlue hover:underline"
                        >
                          {ref.referred?.name || ref.referred?.mobile || "—"}
                        </Link>
                      ) : (
                        ref.referred?.name || ref.referred?.mobile || "—"
                      )}
                    </td>
                    <td className="py-3 px-2">
                      <StatusBadge status={ref.status} />
                    </td>
                    <td className="py-3 px-2 text-gray-500">
                      {new Date(ref.createdOn).toLocaleDateString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-400 text-center py-4">No referrals yet</p>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon, value, label, color, bg }) => (
  <div className={`${bg} rounded-xl p-4 text-center`}>
    <div className={`${color} flex justify-center mb-2`}>{icon}</div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    <p className="text-xs text-gray-500 mt-1">{label}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    rewarded: "bg-green-100 text-green-700",
    active: "bg-yellow-100 text-yellow-700",
  };
  const labels = {
    rewarded: "Rewarded",
    active: "Active",
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-bold ${styles[status] || "bg-gray-100 text-gray-700"}`}>
      {labels[status] || status}
    </span>
  );
};

export default ReferralDashboard;
